import React, { useContext, useRef, useState } from "react";
import PropTypes from "prop-types";

import { FormInput } from "../FormInput";
import SlideOver from "../../tailwindUI/SlideOver";
import PrimaryButton from "../../tailwindUI/PrimaryButton";

import {
  htmlListToArray,
  fixatServiceFormFields,
  tabDiscountFields,
  textEditorFields,
  useFixatServiceForm,
  tabDiscountValidationRules,
} from "../../hooks/catalogs/useFixatServiceForm";

import { PlusIcon } from "@heroicons/react/24/outline";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { Controller } from "react-hook-form";
import { TipTapEditor } from "./TipTapEditor";
import {
  createService,
  updateService,
} from "../../apiClient/operations/catalogsOperations";
import { AlertContext } from "../../context/AlertContext";
import SecondaryLoader from "../../hooks/SecondaryLoader";
import LongAlert from "../../tailwindUI/LongAlert";

export function FixatServiceSlideForm({
  openSlideForm,
  setOpenSlideForm,
  addService,
  editService,
  serviceToEdit = null,
}) {
  const { setAlert } = useContext(AlertContext);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(null);

  const {
    register,
    control,
    errors,
    reset,
    getValues,
    handleSubmit,
    tabDiscount,
    addDiscount,
    removeDiscount,
  } = useFixatServiceForm(serviceToEdit);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const formattedData = {
        sku: data.sku,
        name: data.name,
        short_name: data.short_name,
        price: data.price * 100,
        currency: data.currency,
        legal_type: data.legal_type,
        periodicity: data.periodicity,
        category: data.category,
        time_periods: data.time_periods,
        description: data.description,
        status: data.status || "active",
        priority: data.priority,
        minimum_periods: data.minimum_periods,
        tab_discount: data.tab_discount.map((discount) => ({
          ...discount,
          max_discount: discount.max_discount * 100,
        })),
        service_info: {
          terms_and_conditions: htmlListToArray(data.terms_and_conditions),
          cancelation_clauses: htmlListToArray(data.cancelation_clauses),
          service_reach: htmlListToArray(data.service_reach),
        },
      };

      if (!serviceToEdit) {
        const createdService = await createService(formattedData);
        addService(createdService);
      } else {
        const editedService = await updateService(
          serviceToEdit.id,
          formattedData,
        );
        editService(editedService);
      }

      onClose();
      setAlert({
        active: true,
        type: "success",
        message: serviceToEdit
          ? "Servicio actualizado correctamente"
          : "Servicio creado correctamente",
      });
    } catch (error) {
      handleScroll();
      setError(error?.message || "Error al procesar tu solicitud");
    } finally {
      setIsLoading(false);
    }
  };

  const handleScroll = () => {
    scrollRef.current?.scrollTo({
      top: 0,
      behavior: "auto",
    });
  };

  const onClose = () => {
    setOpenSlideForm(false);
    reset();
  };

  return (
    <SlideOver
      ref={scrollRef}
      open={openSlideForm}
      setOpen={setOpenSlideForm}
      onClose={onClose}
      title={serviceToEdit ? "Editar servicio" : "Crear servicio"}
      className="w-full sm:max-w-[45rem]"
    >
      {isLoading && <SecondaryLoader />}
      {error && (
        <div className="w-full sticky mb-24 z-[30]">
          <LongAlert
            className="px-0"
            title={error}
            show={error !== null}
            onClose={() => setError(null)}
          />
        </div>
      )}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full divide-y divide-gray-200"
      >
        <div className="w-full grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2 pb-4">
          {fixatServiceFormFields.map((field) => (
            <FormInput
              key={field.name}
              className={field.className}
              name={field.name}
              label={field.label}
              placeholder={field.placeholder}
              variant={field.variant}
              type={field.inputType}
              options={field.options}
              register={register}
              errors={errors}
              rules={field.rules}
            />
          ))}
        </div>

        <div className="py-4">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium mb-2">Descuentos</h3>
            <button
              className="border-b-[1.5px] border-button-orange font-medium text-button-orange flex items-center"
              type="button"
              onClick={addDiscount}
            >
              Agregar descuento
              <PlusIcon className="h-5 w-5 inline-block ml-1" />
            </button>
          </div>

          {tabDiscount.length === 0 && (
            <p className="text-gray-500 mb-2">No hay descuentos agregados.</p>
          )}

          {tabDiscount.map((discount, index) => (
            <div key={discount.id} className="w-full flex justify-between">
              <div className="mr-2 mt-6">
                <button type="button" onClick={() => removeDiscount(index)}>
                  <XCircleIcon className="h-7 w-7 text-v2-input-text" />
                </button>
              </div>
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-6 pb-4">
                {tabDiscountFields.map((field) => (
                  <FormInput
                    key={`${field.name}-${index}`}
                    name={`tab_discount.${index}.${field.name}`}
                    label={field.label}
                    type={field.inputType}
                    variant={field.variant}
                    placeholder={field.placeholder}
                    options={field.options}
                    register={register}
                    errors={errors}
                    rules={tabDiscountValidationRules(
                      field.name,
                      index,
                      getValues,
                    )}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="py-4">
          {textEditorFields.map((field) => (
            <div key={field.name} className="w-full mb-4">
              <Controller
                name={field.name}
                control={control}
                rules={field.rules}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <TipTapEditor
                    label={field.label}
                    value={value}
                    placeholder={field.placeholder}
                    onChange={onChange}
                    error={error?.message}
                  />
                )}
              />
            </div>
          ))}
        </div>

        <div className="col-span-full w-full flex items-center justify-end mt-4 py-6">
          <button
            className="underline rounded-md font-medium text-button-orange mr-6"
            type="button"
            onClick={onClose}
          >
            Cancelar
          </button>
          <div className="w-[15rem]">
            <PrimaryButton type="submit" isFullWidth>
              {serviceToEdit ? "Actualizar servicio" : "Crear servicio"}
              {!serviceToEdit && (
                <PlusIcon className="w-5 h-5 ml-2 inline-block" />
              )}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </SlideOver>
  );
}

FixatServiceSlideForm.propTypes = {
  openSlideForm: PropTypes.bool.isRequired,
  setOpenSlideForm: PropTypes.func.isRequired,
  addService: PropTypes.func,
  editService: PropTypes.func.isRequired,
  serviceToEdit: PropTypes.object,
};
